import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { Category } from '../../../../shared/issuesV2';
import { useDebouncedCallback, useInputValue } from '../../../hooks';
import * as IssuesV2Store from '../../../redux/issuesV2';
import { useSetFilterHasChanged } from '../../../redux/savedFilters/hooks';
import SearchInput from '../../generic/Forms/SearchInput';

import useFetchIssuesAction from './hooks/useFetchIssuesAction';

//  Vulns allows searching for CVEs, so the placeholder needs to include that text.
const getCategoryPlaceholder = (category: Category): 'Search by package name' | 'Search by CVE or package name' =>
  category === Category.Vulnerability ? 'Search by CVE or package name' : 'Search by package name';

/**
 * IssuesSearch is just a SearchInput which is connected to Redux.
 */
const IssuesSearch = () => {
  const issueCategory = useSelector(IssuesV2Store.selectors.getIssueCategory);
  const search = useSelector(IssuesV2Store.selectors.getSearch);
  const onSearchChange = useFetchIssuesAction(IssuesV2Store.actions.setSearch);
  const setFilterHasChanged = useSetFilterHasChanged();

  // Even though this is debounced internally, we debounce here to prevent hammering the Redux store and causing rerenders.
  const debouncedOnSearchChange = useDebouncedCallback(onSearchChange);

  //  We are aware that we are duplicating state here.
  const [value, setSearch] = useInputValue(search ?? '');
  //  Catch changes to the Redux store. Again, duplicating state is not a good pattern,
  //  but we wanted to avoid hammering actions to the Redux store.
  useEffect(() => {
    setSearch(search ?? '');
  }, [search, setSearch]);

  const onChange = (nextValue: string) => {
    //  Reflect the changes in the state right away...
    setSearch(nextValue);
    setFilterHasChanged(true);
    //  ...then send a debounced update to the Redux store.
    debouncedOnSearchChange(nextValue);
  };

  return (
    <div className="issues-inbox__search">
      <SearchInput
        label="Search for issues" // Label is just for a11y purposes and is not visible.
        size="small"
        placeholder={getCategoryPlaceholder(issueCategory)}
        value={value}
        onChange={onChange}
        testId="issues-inbox-searchbar"
      />
    </div>
  );
};

export default IssuesSearch;
